import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  Divider,
  Alert,
} from '@mui/material';
import { AdminAuditLogEntry } from '../../../../types/admin';
import { format } from 'date-fns';

interface AuditLogDetailModalProps {
  open: boolean;
  entry: AdminAuditLogEntry;
  onClose: () => void;
}

export default function AuditLogDetailModal({ open, entry, onClose }: AuditLogDetailModalProps) {
  const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={4}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Typography variant="body2">{value}</Typography>
      </Grid>
    </Grid>
  );

  const actionTypeColor = {
    view: 'default' as const,
    create: 'success' as const,
    update: 'primary' as const,
    delete: 'error' as const,
    export: 'warning' as const,
    bulk_operation: 'secondary' as const,
    system_config: 'info' as const,
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Audit Log Entry
        <Typography variant="body2" color="text.secondary">
          {entry.id}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {!entry.success && (
          <Alert severity="error" sx={{ mb: 3 }}>
            This action failed: {entry.error_message}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Admin Information
          </Typography>
          <DetailRow label="Admin Name" value={entry.admin_name} />
          <DetailRow label="Admin Email" value={entry.admin_email} />
          <DetailRow label="Admin User ID" value={entry.admin_user_id} />
          <DetailRow label="IP Address" value={entry.ip_address} />
          <DetailRow
            label="User Agent"
            value={
              <Box
                sx={{
                  maxWidth: 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {entry.user_agent}
              </Box>
            }
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Action Details
          </Typography>
          <DetailRow
            label="Action Type"
            value={
              <Chip
                label={entry.action_type.toUpperCase()}
                size="small"
                color={actionTypeColor[entry.action_type] || 'default'}
              />
            }
          />
          <DetailRow label="Description" value={entry.action_description} />
          <DetailRow
            label="Entity Type"
            value={<Chip label={entry.entity_type.toUpperCase()} size="small" variant="outlined" />}
          />
          {entry.entity_id && <DetailRow label="Entity ID" value={entry.entity_id} />}
          {entry.entity_name && <DetailRow label="Entity Name" value={entry.entity_name} />}
          <DetailRow
            label="Status"
            value={
              <Chip
                label={entry.success ? 'SUCCESS' : 'FAILED'}
                size="small"
                color={entry.success ? 'success' : 'error'}
              />
            }
          />
          <DetailRow label="Execution Time" value={`${entry.execution_time_ms}ms`} />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Additional Details
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              maxHeight: 300,
              overflow: 'auto',
            }}
          >
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
              {JSON.stringify(entry.action_details, null, 2)}
            </pre>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Timestamp
          </Typography>
          <DetailRow label="Created At" value={format(new Date(entry.created_at), 'PPP p')} />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
